import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import { useMemo } from "react";
import type { Annotation } from "./useAnnotations";

const useAnnotationDisplayState = createGlobalState({
  storage: "local",
  key: "AnnotationDisplayState",
  type: io.record(io.string, io.array(io.string)),
  scoped: true,
});

export function useAnnotationDisplay(
  blockId: string,
  annotations: Annotation[],
  { displayAll = false }: { displayAll?: boolean } = {},
) {
  const [annotationsDisplay, setAnnotationsDisplay] = useAnnotationDisplayState({
    scope: blockId,
    defaultValue: {
      validity: [
        "VALID_AUTO",
        "VALID_MANUAL",
        "VALID_PARTIAL_INPUTS",
        "INVALID_AUTO",
        "INVALID_MANUAL",
        "INVALID_AUTO_CROSS_FILTER",
        "INVALID_INHERITED",
      ],
      faultStatus: ["CONFIRMED", "TO_BE_ANALYSED", "SUSPECTED", "NON_MODELLED"],
    },
  });

  const displayedAnnotations = useMemo(() => {
    if (displayAll) return annotations.map(() => true);

    const notDetectedVisible = annotationsDisplay["faultStatus"]?.includes("NOT_DETECTED");

    return annotations.map(annotation => {
      const hasMultipleFaultStatuses = annotation.values.some(v => v.hasMultipleFaultStatuses);
      const faultStatus = annotation.values.find(v => v.column === "faultStatus")?.value;
      const notDetectedFault =
        annotation.type === "FAULT" &&
        faultStatus !== "REJECTED" &&
        faultStatus !== "LEGACY_REJECTED" &&
        annotation.values
          .filter(({ column }) => column !== "faultStatus")
          .every(({ value }) => value === null);

      const displayableValues = annotation.values.filter(v =>
        Object.hasOwn(annotationsDisplay, v.column),
      );
      const statusVisible =
        hasMultipleFaultStatuses ||
        displayableValues.every(({ column, value }) =>
          value ? annotationsDisplay[column]?.includes(value.toString()) : false,
        );
      if (notDetectedFault) return notDetectedVisible && (faultStatus ? statusVisible : true);
      return statusVisible;
    });
  }, [annotations, annotationsDisplay, displayAll]);

  return { displayedAnnotations, annotationsDisplay, setAnnotationsDisplay } as const;
}
